/** Admin > Projects: every project with its size, whether it syncs, who owns it, and the recycle bin and clean-up actions. */
import { ChevronRight, ExternalLink, RefreshCw } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { Field, Modal, Spinner } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { fileList, formatDate, timeAgo, toast, useSession } from "../lib/app";
import { count, formatBytes, percent, syncProblem, URLS, useResource, type ProjectUsage, type StorageReport, type SyncResponse, type SyncRow, type UsersResponse } from "./data";
import { AdminLink, Card, Loading, PageHead, Segmented, Switch, useConfirm } from "./ui";

type View = "all" | "syncing" | "off" | "bin";
type Sort = "edited" | "size" | "name";

interface Row {
  id: string;
  name: string;
  owner: string;
  members: number | null;
  updatedAt: number;
  deletedAt: number | null;
  usage: ProjectUsage | null;
  sync: SyncRow | null;
}

interface SyncOutcome {
  status: "ok" | "error" | "local";
  error?: string;
  kept?: string[];
}

/** Storage rows (every project, binned ones too) joined with sync rows (the sync owner only). */
function joinRows(storage: StorageReport | undefined, sync: SyncResponse | undefined): Row[] {
  const rows = new Map<string, Row>();
  for (const p of storage?.projects ?? [])
    rows.set(p.id, { id: p.id, name: p.name, owner: p.owner, members: p.members, updatedAt: p.updatedAt, deletedAt: p.deletedAt, usage: p, sync: null });
  for (const s of sync?.projects ?? []) {
    const row = rows.get(s.id);
    if (row) Object.assign(row, { sync: s, name: s.name, updatedAt: Math.max(row.updatedAt ?? 0, s.updatedAt) });
    else
      rows.set(s.id, { id: s.id, name: s.name, owner: s.owner?.username ?? "", members: null, updatedAt: s.updatedAt, deletedAt: null, usage: null, sync: s });
  }
  return [...rows.values()];
}

export function ProjectsPage() {
  const { canManageSync } = useSession();
  const storage = useResource<StorageReport>(URLS.storage);
  const sync = useResource<SyncResponse>(canManageSync ? URLS.sync : null);
  const [params] = useState(() => new URLSearchParams(location.search));
  const [view, setView] = useState<View>("all");
  const [sort, setSort] = useState<Sort>(params.get("sort") === "size" ? "size" : "edited");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(params.get("show") ? [params.get("show")!] : []));
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [scanning, setScanning] = useState(false);
  const [confirmDialog, confirm] = useConfirm();
  const [owning, setOwning] = useState<Row | null>(null);

  const rows = joinRows(storage.data, canManageSync ? sync.data : undefined);
  const live = rows.filter((r) => !r.deletedAt);
  const binned = rows.length - live.length;
  const syncing = live.filter((r) => r.sync?.repo).length;
  const largest = Math.max(1, ...rows.map((r) => r.usage?.total ?? 0));
  const disk = storage.data?.disk;
  const ready = !!storage.data || (canManageSync && !!sync.data);

  // A link from elsewhere (?show=id) opens that project's details; scroll to it once it's drawn.
  const shown0 = params.get("show");
  useEffect(() => {
    if (shown0 && ready) document.getElementById(`project-${shown0}`)?.scrollIntoView({ block: "center" });
  }, [ready]);

  const q = filter.trim().toLowerCase();
  const shown = rows
    .filter((r) =>
      view === "bin" ? !!r.deletedAt : view === "syncing" ? !r.deletedAt && !!r.sync?.repo : view === "off" ? !r.deletedAt && !r.sync?.repo : true,
    )
    .filter((r) => !q || [r.name, r.owner, r.sync?.repo?.name].some((s) => s?.toLowerCase().includes(q)))
    .sort((a, b) =>
      sort === "size" ? (b.usage?.total ?? 0) - (a.usage?.total ?? 0) : sort === "name" ? a.name.localeCompare(b.name) : b.updatedAt - a.updatedAt,
    );

  const toggle = (id: string) =>
    setOpen((s) => {
      const next = new Set(s);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  async function rescan() {
    setScanning(true);
    try {
      await api.get(`${URLS.storage}?fresh=1`);
      await storage.reload();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    setScanning(false);
  }

  async function flip(row: Row) {
    const s = row.sync!;
    const enabled = !s.repo;
    setBusy((b) => new Set(b).add(row.id));
    try {
      const r = await api.post<{ project: SyncRow; outcome?: SyncOutcome }>(`/api/admin/sync/${row.id}`, { enabled });
      sync.update((d) => ({ ...d, projects: d.projects.map((x) => (x.id === row.id ? r.project : x)) }));
      const o = r.outcome;
      const repo = r.project.repo?.name;
      if (!enabled) toast(`Stopped syncing "${row.name}". Its repository is still there.`, "success");
      else if (o?.status === "error") toast(`"${row.name}" is linked to ${repo}, but the first sync failed: ${o.error}`, "error");
      else if (o?.kept?.length)
        toast(`"${row.name}" now syncs to ${repo}. ${fileList(o.kept)} had changed on both sides; benleaf's version was kept.`, "success");
      else toast(`"${row.name}" now syncs to ${repo}`, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
      sync.reload();
    } finally {
      setBusy((b) => {
        const next = new Set(b);
        next.delete(row.id);
        return next;
      });
    }
  }

  async function clearCache(row: Row) {
    const ok = await confirm({
      title: `Clear the compile cache for “${row.name}”?`,
      body: <p>This frees {formatBytes(row.usage!.cache)}. Nothing in the project is lost, but its next compile starts from scratch and takes longer.</p>,
      confirm: "Clear cache",
    });
    if (!ok) return;
    try {
      await api.post(`/api/admin/storage/projects/${row.id}/clear-cache`);
      toast(`Cleared ${formatBytes(row.usage!.cache)} of compile cache`, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    storage.reload();
  }

  async function purge(row: Row) {
    const ok = await confirm({
      title: `Delete “${row.name}” for good?`,
      body: (
        <p>
          It's in @{row.owner}'s recycle bin. Deleting it now frees {formatBytes(row.usage?.total ?? 0)}, and they won't be able to restore it. This can't be
          undone.
        </p>
      ),
      confirm: "Delete for good",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.post(`/api/admin/storage/projects/${row.id}/purge`);
      toast(`Deleted "${row.name}" and freed ${formatBytes(row.usage?.total ?? 0)}`, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    storage.reload();
  }

  async function bin(row: Row) {
    const others = row.members && row.members > 1 ? row.members - 1 : 0;
    const ok = await confirm({
      title: `Move “${row.name}” to the recycle bin?`,
      body: (
        <p>
          @{row.owner}
          {others ? ` and the ${others === 1 ? "person" : `${others} people`} it's shared with lose` : " loses"} access straight away. It can be restored, by them or from
          here, until the recycle bin empties itself.
        </p>
      ),
      confirm: "Move to recycle bin",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.post(`/api/admin/projects/${row.id}/bin`);
      toast(`Moved "${row.name}" to @${row.owner}'s recycle bin`, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    reloadAll();
  }

  async function restore(row: Row) {
    try {
      await api.post(`/api/admin/projects/${row.id}/restore`);
      toast(`Restored "${row.name}" for @${row.owner}`, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    reloadAll();
  }

  function reloadAll() {
    storage.reload();
    if (canManageSync) sync.reload();
  }

  const views: Array<{ value: View; label: string; count: number }> = [{ value: "all", label: "All", count: rows.length }];
  if (canManageSync && sync.data)
    views.push({ value: "syncing", label: "Syncing", count: syncing }, { value: "off", label: "Not syncing", count: live.length - syncing });
  if (binned || view === "bin") views.push({ value: "bin", label: "Recycle bin", count: binned });
  const columns = canManageSync ? 3 : 2;

  return (
    <>
      <PageHead
        title="Projects"
        lede={
          storage.data
            ? `${count(live.length, "project")}${binned ? ` and ${binned} in recycle bins` : ""}, taking ${formatBytes(rows.reduce((a, r) => a + (r.usage?.total ?? 0), 0))} on disk.`
            : "Every project, how much space it takes, and what can be cleared."
        }
        actions={
          <button className="btn" onClick={rescan} disabled={scanning} title="Measure every project's size again">
            {scanning ? <Spinner size={12} /> : <RefreshCw size={14} />} Rescan sizes
          </button>
        }
      />

      <Card flush>
        <div className="admin-toolbar">
          {views.length > 1 && <Segmented label="Show" value={view} options={views} onChange={setView} />}
          <span className="grow" />
          <input
            type="search"
            className="admin-filter"
            placeholder={canManageSync ? "Filter by name, owner or repository" : "Filter by name or owner"}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter projects"
          />
          <label className="admin-sort">
            <span className="muted small">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              <option value="edited">Last edited</option>
              <option value="size">Largest first</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>
        {canManageSync && (
          <p className="admin-intro">
            Switch sync on to back a project up to a new private repository called <code>benleaf-&lt;project name&gt;</code>
            {sync.data?.account ? (
              <>
                {" "}
                on <b>{sync.data.account}</b>
              </>
            ) : null}
            . Switching it off stops syncing but keeps the repository; switch it back on to carry on where it left off.
          </p>
        )}

        {!ready ? (
          storage.error ? (
            <p className="error-text admin-pad">{storage.error}</p>
          ) : (
            <Loading>Measuring projects…</Loading>
          )
        ) : (
          <div className="admin-scroll">
            <table className={`table admin-table projects-table${canManageSync ? " with-sync" : ""}`}>
              <thead>
                <tr>
                  <th>Project</th>
                  <th className="size-col">Size</th>
                  {canManageSync && <th className="sync-col">Sync</th>}
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={columns} className="muted">
                      {rows.length ? "No projects match." : "No projects yet."}
                    </td>
                  </tr>
                )}
                {shown.map((r) => {
                  const isOpen = open.has(r.id);
                  const u = r.usage;
                  return (
                    <Fragment key={r.id}>
                      <tr
                        id={`project-${r.id}`}
                        className={`proj-row${isOpen ? " open" : ""}${r.deletedAt ? " binned" : ""}`}
                        onClick={(e) => {
                          if (!(e.target as HTMLElement).closest("button, a, input, select")) toggle(r.id);
                        }}
                      >
                        <td>
                          <div className="project-cell">
                            <button
                              type="button"
                              className="icon-btn expand-btn"
                              aria-expanded={isOpen}
                              aria-label={`Details for ${r.name}`}
                              onClick={() => toggle(r.id)}
                            >
                              <ChevronRight size={16} />
                            </button>
                            <div className="project-text">
                              <div className="project-name">
                                <b>{r.name}</b>
                                {r.deletedAt && <span className="chip chip-warn">In recycle bin</span>}
                              </div>
                              <div className="muted small">
                                @{r.owner || "unknown"}
                                {r.members && r.members > 1 ? ` · shared with ${r.members - 1}` : ""}
                                {r.deletedAt ? ` · binned ${timeAgo(r.deletedAt)}` : ` · edited ${timeAgo(r.updatedAt)}`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="size-col">
                          {u ? (
                            <div className="size-cell" title={`${formatBytes(u.total)}, ${percent(u.total, disk?.total ?? 0)} of the disk`}>
                              <span className="size-total">{formatBytes(u.total)}</span>
                              <span className="share-track" aria-hidden="true">
                                {u.total - u.cache > 0 && (
                                  <span className="share-fill seg-files" style={{ width: `${((u.total - u.cache) / largest) * 100}%` }} />
                                )}
                                {u.cache > 0 && <span className="share-fill seg-cache" style={{ width: `${(u.cache / largest) * 100}%` }} />}
                              </span>
                            </div>
                          ) : (
                            <span className="muted">–</span>
                          )}
                        </td>
                        {canManageSync && (
                          <td className="sync-col">
                            {r.sync && (
                              <div className="sync-cell">
                                <SyncStatus row={r.sync} />
                                <Switch
                                  on={!!r.sync.repo}
                                  busy={busy.has(r.id)}
                                  label={`Sync ${r.name}`}
                                  title={r.sync.repo ? "Stop syncing (the repository is kept)" : "Start syncing this project"}
                                  onChange={() => flip(r)}
                                />
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                      {isOpen && (
                        <tr className="project-detail">
                          <td colSpan={columns}>
                            <Details
                              row={r}
                              diskTotal={disk?.total ?? 0}
                              account={sync.data?.account ?? null}
                              onClearCache={() => clearCache(r)}
                              onPurge={() => purge(r)}
                              onBin={() => bin(r)}
                              onRestore={() => restore(r)}
                              onChangeOwner={() => setOwning(r)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {storage.data && (
        <p className="admin-foot muted small">
          Bars compare each project with the largest: <i className="swatch seg-files" /> files, history and PDFs, <i className="swatch seg-cache" /> compile
          cache. Measured {timeAgo(storage.data.scannedAt)}; the disk as a whole is on the <AdminLink to="/admin/storage">Storage</AdminLink> page.
        </p>
      )}
      {confirmDialog}
      {owning && (
        <OwnerDialog
          row={owning}
          onClose={() => setOwning(null)}
          onDone={() => {
            setOwning(null);
            reloadAll();
          }}
        />
      )}
    </>
  );
}

/** Hands a project to someone else, e.g. before its owner's account is deleted. */
function OwnerDialog({ row, onClose, onDone }: { row: Row; onClose: () => void; onDone: () => void }) {
  const users = useResource<UsersResponse>(URLS.users);
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const people = (users.data?.users ?? []).filter((u) => u.username !== row.owner).sort((a, b) => a.displayName.localeCompare(b.displayName));

  async function save() {
    setBusy(true);
    try {
      await api.post(`/api/admin/projects/${row.id}/owner`, { username });
      toast(`"${row.name}" now belongs to @${username}`, "success");
      onDone();
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Change the owner of “${row.name}”`}
      onClose={onClose}
      width={460}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!username || busy} onClick={save}>
            {busy && <Spinner size={12} />} Change owner
          </button>
        </>
      }
    >
      <Field label="New owner" hint={`@${row.owner} stays on as an editor. The new owner can share it, change its settings and delete it.`}>
        <select autoFocus value={username} disabled={!users.data} onChange={(e) => setUsername(e.target.value)}>
          <option value="" disabled>
            {users.data ? "Choose someone" : "Loading…"}
          </option>
          {people.map((u) => (
            <option key={u.id} value={u.username}>
              {u.displayName} (@{u.username})
            </option>
          ))}
        </select>
      </Field>
      {users.error && <p className="error-text">{users.error}</p>}
    </Modal>
  );
}

function SyncStatus({ row }: { row: SyncRow }) {
  const s = row.syncState;
  if (!row.repo) return <span className="muted small">Off</span>;
  if (syncProblem(row))
    return (
      <span className="chip chip-bad" title={s.lastError}>
        <span className="dot" /> {s.status === "conflict" ? "Conflict" : "Failing"}
      </span>
    );
  if (!s.lastSyncAt) return <span className="chip">Waiting</span>;
  return (
    <span className="chip chip-ok" title={row.autoSync ? "Syncs on its own after edits" : "Only syncs when someone saves a version"}>
      <span className="dot" /> {timeAgo(s.lastSyncAt)}
      {!row.autoSync && " · manual"}
    </span>
  );
}

/** The opened-up row: where the space goes, sync details and the actions. */
function Details({
  row,
  diskTotal,
  account,
  onClearCache,
  onPurge,
  onBin,
  onRestore,
  onChangeOwner,
}: {
  row: Row;
  diskTotal: number;
  account: string | null;
  onClearCache: () => void;
  onPurge: () => void;
  onBin: () => void;
  onRestore: () => void;
  onChangeOwner: () => void;
}) {
  const u = row.usage;
  const s = row.sync;
  return (
    <div className="detail">
      {u && (
        <div className="detail-block">
          <h3>Space</h3>
          <dl className="detail-list">
            <dt>Files</dt>
            <dd>{formatBytes(u.files)}</dd>
            <dt>Version history</dt>
            <dd>{formatBytes(u.history)}</dd>
            <dt>PDFs and logs</dt>
            <dd>{formatBytes(u.builds)}</dd>
            <dt title="LaTeX's intermediate files, kept so the next compile is quicker">Compile cache</dt>
            <dd>{formatBytes(u.cache)}</dd>
            <dt className="total">In all</dt>
            <dd className="total">
              {formatBytes(u.total)} <span className="muted">· {percent(u.total, diskTotal)} of the disk</span>
            </dd>
          </dl>
        </div>
      )}
      {s && (
        <div className="detail-block">
          <h3>Sync</h3>
          {s.repo ? (
            <dl className="detail-list">
              <dt>Repository</dt>
              <dd>
                <a href={s.repo.url} target="_blank" rel="noreferrer noopener" className="mono">
                  {s.repo.name} <ExternalLink size={12} />
                </a>
              </dd>
              <dt>Branch</dt>
              <dd className="mono">{s.repo.branch}</dd>
              <dt>When</dt>
              <dd>{s.autoSync ? "On its own, after edits" : "Only when someone saves a version"}</dd>
              <dt>Last synced</dt>
              <dd>{s.syncState.lastSyncAt ? `${timeAgo(s.syncState.lastSyncAt)} (${formatDate(s.syncState.lastSyncAt)})` : "Not yet"}</dd>
              {syncProblem(s) && (
                <>
                  <dt>Problem</dt>
                  <dd className="error-text">
                    {s.syncState.lastError || "The last sync failed."}
                    {s.syncState.conflictBranch && <div className="mono small">Branch {s.syncState.conflictBranch}</div>}
                  </dd>
                </>
              )}
              {s.syncState.kept && (
                <>
                  <dt>Kept</dt>
                  <dd title={s.syncState.kept.files.join("\n")}>
                    benleaf's {fileList(s.syncState.kept.files)}, {timeAgo(s.syncState.kept.at)}, when both sides had changed
                  </dd>
                </>
              )}
            </dl>
          ) : (
            <p className="muted detail-note">
              Not syncing.{" "}
              {s.syncState.formerRepo ? (
                <>
                  Switching it on reconnects to <span className="mono">{s.syncState.formerRepo}</span>, the repository it used before.
                </>
              ) : (
                <>
                  Switching it on creates{" "}
                  <span className="mono">
                    {account ? `${account}/` : ""}
                    {s.next}
                  </span>{" "}
                  (with -2, -3, … on the end if that name is taken).
                </>
              )}
            </p>
          )}
        </div>
      )}
      <div className="detail-actions">
        {row.deletedAt && (
          <p className="muted small">
            In @{row.owner}'s recycle bin since {formatDate(row.deletedAt)}. They can still restore it until the bin empties itself.
          </p>
        )}
        <div className="row">
          <AdminLink to={`/admin/audit?project=${encodeURIComponent(row.id)}`} className="btn btn-sm">
            See activity
          </AdminLink>
          {u && u.cache > 0 && (
            <button
              className="btn btn-sm"
              title="Free the cache's space. The project's next compile starts from scratch, so it's slower."
              onClick={onClearCache}
            >
              Clear compile cache
            </button>
          )}
          {row.deletedAt ? (
            <>
              <button className="btn btn-sm" title={`Put it back for @${row.owner} and everyone it's shared with`} onClick={onRestore}>
                Restore
              </button>
              <button className="btn btn-sm btn-danger" title="Delete it for good now instead of waiting for the recycle bin to empty" onClick={onPurge}>
                Delete for good
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-sm" title="Hand it to someone else. The current owner stays on as an editor." onClick={onChangeOwner}>
                Change owner…
              </button>
              <button className="btn btn-sm btn-danger" title="Take it away from everyone. It can be restored until the recycle bin empties." onClick={onBin}>
                Move to recycle bin
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
